import { ContactValue, DetailSection, Field, FieldGrid } from '@/components/admin/detail-parts';
import { type AdminTalentDetail } from '@/contracts';
import { useCityLabel } from '@/i18n/use-city-label';
import { useTranslations } from '@/i18n/use-locale';
import { ageFromDateOfBirth, isoDate } from '@/lib/detail';
import { useGenderLabel, useIdentityKindLabel, useMaritalStatusLabel } from '@/lib/labels';
import { useNationalityLabel } from '@/i18n/use-nationality-label';

export function ContactSection({ talent }: { talent: AdminTalentDetail }) {
  const t = useTranslations('admin.talentDetail');
  const common = useTranslations('common');
  const identityKindLabel = useIdentityKindLabel();

  return (
    <DetailSection title={t('section.contact')}>
      <FieldGrid>
        <Field label={t('field.phone')}>
          {talent.phone && <ContactValue value={talent.phone} copyLabel={t('copyPhone')} numeric />}
        </Field>
        <Field label={t('field.email')}>
          {talent.email && <ContactValue value={talent.email} copyLabel={common('copyEmail')} />}
        </Field>
        <Field label={t('field.identityKind')}>
          {talent.identityKind && identityKindLabel(talent.identityKind)}
        </Field>
      </FieldGrid>
    </DetailSection>
  );
}

export function PersonalSection({ talent }: { talent: AdminTalentDetail }) {
  const t = useTranslations('admin.talentDetail');
  const genderLabel = useGenderLabel();
  const maritalStatusLabel = useMaritalStatusLabel();
  const cityLabel = useCityLabel();
  const age = ageFromDateOfBirth(talent.dateOfBirth);
  const nationality = useNationalityLabel(talent.nationality);

  return (
    <DetailSection title={t('section.personal')}>
      <FieldGrid>
        <Field label={t('field.age')}>
          {age !== null && (
            <span className="num">
              {t('ageValue', { age, date: isoDate(talent.dateOfBirth) ?? '' })}
            </span>
          )}
        </Field>
        <Field label={t('field.gender')}>{talent.gender && genderLabel(talent.gender)}</Field>
        <Field label={t('field.maritalStatus')}>
          {talent.maritalStatus && maritalStatusLabel(talent.maritalStatus)}
        </Field>
        <Field label={t('field.nationality')}>{nationality}</Field>
        <Field label={t('field.city')}>{cityLabel(talent.city)}</Field>
      </FieldGrid>
    </DetailSection>
  );
}

export function ProfileSection({
  talent,
  specialty,
}: {
  talent: AdminTalentDetail;
  specialty: string;
}) {
  const t = useTranslations('admin.talentDetail');
  const admin = useTranslations('admin');
  const completion = talent.profileCompletionPercent;

  return (
    <DetailSection title={t('section.profile')}>
      <FieldGrid>
        <Field label={t('field.specialty')}>{specialty}</Field>
        <Field label={t('field.experience')}>
          <span className="num">
            {admin('table.yearsValue', { count: talent.yearsOfExperience })}
          </span>
        </Field>
        <Field label={t('field.verifiedAt')}>
          {talent.verifiedAt && <span className="num">{isoDate(talent.verifiedAt)}</span>}
        </Field>
        <Field label={t('field.completion')}>
          {completion !== undefined && (
            <span className="flex items-center gap-2">
              <span
                role="progressbar"
                aria-valuenow={completion}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('field.completion')}
                className="h-1.5 w-24 overflow-hidden rounded-full bg-conc-50"
              >
                <span
                  className="block h-full rounded-full bg-pitch"
                  style={{ width: `${Math.max(0, Math.min(100, completion))}%` }}
                />
              </span>
              <span className="num">{t('completionValue', { percent: completion })}</span>
            </span>
          )}
        </Field>
        <Field label={t('field.headline')} className="sm:col-span-2">
          {talent.headline && <span dir="auto">{talent.headline}</span>}
        </Field>
        <Field label={t('field.bio')} className="sm:col-span-2">
          {talent.bio && (
            <span dir="auto" className="block font-normal whitespace-pre-wrap text-conc-600">
              {talent.bio}
            </span>
          )}
        </Field>
      </FieldGrid>
    </DetailSection>
  );
}
